/**
 * authStorage.js — the ONE decision about where auth state lives. v242.87.0
 *
 * ★★★ IT EXISTS BECAUSE THE DECISION WAS PREVIOUSLY MADE IN TWO PLACES, and two
 * places is how they drift. `services/api.js` picked storage for the token and
 * `stores/index.js` picked it for the user object; both said sessionStorage,
 * both cited "v187.0: HIPAA", and neither could be changed without remembering
 * the other existed. Anything that needs to know where auth lives imports this.
 *
 * The rule: a NATIVE app is its own sandbox, so localStorage there is exactly as
 * isolated as sessionStorage and additionally survives the WKWebView content
 * process being recycled — which iOS does whenever the app hands control to the
 * share sheet or a top-level navigation. Losing auth on that is what stranded
 * patients at the sign-in screen (TF b34 #13 and #17).
 *
 * On WEB, a browser genuinely can be shared, so sessionStorage stays.
 *
 * ★ Every write goes to the active store and every clear goes to BOTH, so a
 *   value written under one policy can never be resurrected after the policy
 *   changes — including on a build where a user upgrades across this change.
 *
 * ★ TODO when the release window allows: swap the body of `store()` for
 *   @capacitor/preferences (Keychain-backed on iOS). Deliberately not done days
 *   before an Apple review, because it adds a CocoaPod and a native rebuild.
 */
import { Capacitor } from '@capacitor/core'

export function isNativeApp() {
  try { return Capacitor?.isNativePlatform?.() === true } catch { return false }
}

function store() {
  try { return isNativeApp() ? window.localStorage : window.sessionStorage } catch { return null }
}

export function authGet(key) {
  try { return store()?.getItem(key) ?? null } catch { return null }
}

export function authSet(key, value) {
  try { store()?.setItem(key, value) } catch (_) {}
}

/** Clears BOTH stores — see the note above about values written under an older policy. */
export function authRemove(key) {
  try { window.sessionStorage.removeItem(key) } catch (_) {}
  try { window.localStorage.removeItem(key) } catch (_) {}
}
